// @ts-nocheck
import { useEffect, useState, useRef } from 'react';
import { YStack, XStack, Text } from 'tamagui';

const VIGNETTE_HEIGHT = 200;

/* ─── Torch: Animated search bar cycling through sources ─── */
const searchQueries = [
  { query: 'Search Gmail...', results: [
    { title: 'Q3 board meeting notes', source: 'Gmail' },
    { title: 'Invoice from AWS', source: 'Gmail' },
  ]},
  { query: 'Search Slack...', results: [
    { title: 'Deploy pipeline discussion', source: 'Slack' },
    { title: '#incidents — DB failover', source: 'Slack' },
  ]},
  { query: 'Search Notion...', results: [
    { title: 'API rate limiting RFC', source: 'Notion' },
    { title: 'Onboarding checklist', source: 'Notion' },
  ]},
  { query: 'Search Drive...', results: [
    { title: 'Product roadmap 2026', source: 'Drive' },
    { title: 'Brand guidelines v3', source: 'Drive' },
  ]},
];

export function TorchVignette({ accent }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setIdx((i) => (i + 1) % searchQueries.length), 2500);
    return () => clearInterval(interval);
  }, []);
  const current = searchQueries[idx];

  return (
    <YStack backgroundColor="$color1" borderRadius="$3" borderWidth={1} borderColor="$color4" padding="$3" opacity={0.95} gap="$2" width={320} height={VIGNETTE_HEIGHT} justifyContent="flex-start">
      <XStack backgroundColor="$color3" borderRadius="$2" paddingHorizontal="$3" paddingVertical="$2" alignItems="center" gap="$2">
        <Text fontSize={12} color="$color6">⌘K</Text>
        <Text fontSize={13} color="$color7" fontFamily="monospace" style={{ transition: 'opacity 0.4s' }} key={idx}>
          {current.query}
        </Text>
      </XStack>
      <YStack gap="$1" paddingHorizontal="$1" style={{ transition: 'opacity 0.3s' }} key={`r-${idx}`}>
        {current.results.map((r) => (
          <XStack key={r.title} gap="$2" alignItems="center" paddingVertical="$1">
            <Text fontSize={10} color={accent}>●</Text>
            <Text fontSize={12} color="$color9" flex={1}>{r.title}</Text>
            <Text fontSize={10} color="$color6">{r.source}</Text>
          </XStack>
        ))}
      </YStack>
    </YStack>
  );
}

/* ─── Statio: Animated key exposure counter ─── */
export function StatioVignette({ accent }) {
  const [keysBlocked, setKeysBlocked] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setKeysBlocked((k) => k + Math.floor(Math.random() * 3) + 1), 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <YStack backgroundColor="$color1" borderRadius="$3" borderWidth={1} borderColor="$color4" padding="$3" opacity={0.95} gap="$2" width={320} height={VIGNETTE_HEIGHT} justifyContent="flex-start">
      <XStack gap="$3">
        <YStack flex={1} gap="$1">
          <Text fontSize={10} color="$red9" fontWeight="600">WITHOUT</Text>
          <YStack backgroundColor="$color3" borderRadius="$2" padding="$2">
            <Text fontSize={11} fontFamily="monospace" color="$color8" lineHeight={16}>
              {'STRIPE_KEY=sk_live_...\nGITHUB_TOKEN=ghp_...\nSLACK_TOKEN=xoxb_...'}
            </Text>
          </YStack>
        </YStack>
        <YStack flex={1} gap="$1">
          <Text fontSize={10} color={accent} fontWeight="600">WITH STATIO</Text>
          <YStack backgroundColor="$color3" borderRadius="$2" padding="$2">
            <Text fontSize={11} fontFamily="monospace" color="$color8" lineHeight={16}>
              {'STATIO_TOKEN=eyJ...'}
            </Text>
          </YStack>
        </YStack>
      </XStack>
      <XStack justifyContent="space-between" alignItems="center" marginTop="auto">
        <Text fontSize={10} color="$color6">Secure tool requests today</Text>
        <Text fontSize={16} color={accent} fontWeight="700" fontFamily="monospace">{keysBlocked.toLocaleString()}</Text>
      </XStack>
    </YStack>
  );
}

/* ─── Denarius: Signal stream ─── */
const signals = [
  { source: 'EDGAR', signal: 'AAPL 10-K annual report filed' },
  { source: 'FMP', signal: 'NVDA earnings beat +12.4%' },
  { source: 'EDGAR', signal: 'MSFT insider sale 50K shares' },
  { source: 'XBRL', signal: 'TSLA debt schedule extracted' },
  { source: 'FMP', signal: 'AMZN revenue miss -3.2%' },
  { source: 'EDGAR', signal: 'GOOG 8-K material event' },
  { source: 'FMP', signal: 'JPM dividend yield → 2.8%' },
  { source: 'EDGAR', signal: 'META equity comp $4.1B' },
  { source: 'MACRO', signal: 'Fed rate hold 5.25-5.50%' },
  { source: 'FMP', signal: 'COST P/E ratio → 52.3' },
  { source: 'EDGAR', signal: 'V 10-Q quarterly filing' },
  { source: 'XBRL', signal: 'WMT cash flow extracted' },
  { source: 'FMP', signal: 'DIS free cash flow +18%' },
  { source: 'EDGAR', signal: 'CRM insider purchase 12K' },
  { source: 'MACRO', signal: 'CPI 3.1% YoY Feb 2026' },
  { source: 'FMP', signal: 'AMD gross margin → 51.2%' },
  { source: 'EDGAR', signal: 'NFLX 8-K subscriber update' },
  { source: 'XBRL', signal: 'BA pension liability $18.2B' },
  { source: 'FMP', signal: 'UNH book value +9.4%' },
  { source: 'MACRO', signal: '10Y Treasury yield 4.28%' },
  { source: 'EDGAR', signal: 'SQ debt offering $1.5B' },
  { source: 'FMP', signal: 'PYPL operating margin → 16%' },
  { source: 'XBRL', signal: 'HD inventory turnover 4.8x' },
  { source: 'EDGAR', signal: 'UBER 10-K annual report' },
  { source: 'MACRO', signal: 'Non-farm payrolls +275K' },
  { source: 'FMP', signal: 'SPOT monthly active users +22%' },
  { source: 'EDGAR', signal: 'COIN insider sale 8K shares' },
  { source: 'FMP', signal: 'LLY market cap → $780B' },
];

export function DenariusVignette({ accent }) {
  const [items, setItems] = useState(signals.slice(0, 4));
  const idx = useRef(4);

  useEffect(() => {
    const interval = setInterval(() => {
      setItems((prev) => {
        const next = signals[idx.current % signals.length];
        idx.current += 1;
        return [next, ...prev.slice(0, 3)];
      });
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  return (
    <YStack backgroundColor="$color1" borderRadius="$3" borderWidth={1} borderColor="$color4" padding="$3" opacity={0.95} gap="$2" width={320} height={VIGNETTE_HEIGHT} justifyContent="flex-start">
      <Text fontSize={10} color="$color6" fontWeight="600" letterSpacing={1}>SIGNAL STREAM</Text>
      <YStack gap="$1">
        {items.map((item, i) => (
          <XStack key={`${item.signal}-${i}`} gap="$2" alignItems="center" paddingVertical={3} borderBottomWidth={1} borderBottomColor="$color3" opacity={i === 0 ? 1 : 0.7 - i * 0.1}>
            <Text fontSize={9} color={accent} fontFamily="monospace" fontWeight="600" width={42}>{item.source}</Text>
            <Text fontSize={11} color="$color9" fontFamily="monospace" flex={1} numberOfLines={1}>{item.signal}</Text>
          </XStack>
        ))}
      </YStack>
    </YStack>
  );
}

/* ─── JanusLedger: Transaction stream ─── */
const transactions = [
  { id: '#4,291', account: 'Cash', debit: '$1,200.00', credit: '—' },
  { id: '#4,291', account: 'Revenue', debit: '—', credit: '$1,200.00' },
  { id: '#4,292', account: 'Accounts Recv', debit: '$3,400.00', credit: '—' },
  { id: '#4,292', account: 'Sales', debit: '—', credit: '$3,400.00' },
  { id: '#4,293', account: 'Payroll', debit: '$8,750.00', credit: '—' },
  { id: '#4,293', account: 'Cash', debit: '—', credit: '$8,750.00' },
];

export function LedgerVignette({ accent }) {
  const [step, setStep] = useState(0);
  const round = useRef(0);

  useEffect(() => {
    const maxSteps = 4; // show 2 entries + balanced + pause
    if (step < maxSteps) {
      const delay = step < 2 ? 1200 : step === 2 ? 800 : 3000;
      const timeout = setTimeout(() => setStep(step + 1), delay);
      return () => clearTimeout(timeout);
    } else {
      const timeout = setTimeout(() => {
        round.current = (round.current + 1) % 3;
        setStep(0);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [step]);

  const pair = transactions.slice(round.current * 2, round.current * 2 + 2);
  const txId = pair[0]?.id || '#4,291';

  return (
    <YStack backgroundColor="$color1" borderRadius="$3" borderWidth={1} borderColor="$color4" padding="$3" opacity={0.95} gap="$2" width={320} height={VIGNETTE_HEIGHT} justifyContent="flex-start">
      <Text fontSize={10} color="$color6" fontWeight="600" letterSpacing={1}>TRANSACTION {txId}</Text>
      <YStack gap="$1">
        <XStack flexDirection="row" justifyContent="space-between" borderBottomWidth={1} borderBottomColor="$color3" paddingBottom={4}>
          <Text fontSize={11} color="$color7" fontFamily="monospace" flex={1}>Account</Text>
          <Text fontSize={11} color="$color7" fontFamily="monospace" width={80} textAlign="right">Debit</Text>
          <Text fontSize={11} color="$color7" fontFamily="monospace" width={80} textAlign="right">Credit</Text>
        </XStack>
        {pair.slice(0, Math.min(step, 2)).map((e) => (
          <XStack key={e.account + txId} flexDirection="row" justifyContent="space-between" paddingVertical={3}>
            <Text fontSize={12} color="$color9" fontFamily="monospace" flex={1}>{e.account}</Text>
            <Text fontSize={12} color={e.debit !== '—' ? accent : '$color5'} fontFamily="monospace" width={80} textAlign="right">{e.debit}</Text>
            <Text fontSize={12} color={e.credit !== '—' ? accent : '$color5'} fontFamily="monospace" width={80} textAlign="right">{e.credit}</Text>
          </XStack>
        ))}
      </YStack>
      {step >= 3 && (
        <XStack flexDirection="row" justifyContent="flex-end" gap="$2" borderTopWidth={1} borderTopColor="$color3" paddingTop={4} marginTop="auto">
          <Text fontSize={10} color={accent} fontWeight="600">✓ Balanced</Text>
        </XStack>
      )}
    </YStack>
  );
}

/* ─── Pilum: Terminal deploy animation ─── */
const deployLines = [
  { text: '$ pilum deploy --tag v2.4.1', color: 'cmd' },
  { text: '', color: 'dim' },
  { text: '  ● torch-api      → Cloud Run    ✓', color: 'success' },
  { text: '  ● torch-web       → Cloudflare  ✓', color: 'success' },
  { text: '  ● statio-gateway  → Cloud Run    ✓', color: 'success' },
  { text: '  ● scuta-cli       → Homebrew    ✓', color: 'success' },
  { text: '', color: 'dim' },
  { text: '  4 targets deployed in 38s', color: 'summary' },
];

export function PilumVignette({ accent }) {
  const [lines, setLines] = useState(0);
  const round = useRef(0);

  useEffect(() => {
    if (lines < deployLines.length) {
      const delay = lines === 0 ? 600 : lines >= 2 && lines <= 5 ? 500 : 300;
      const timeout = setTimeout(() => setLines(lines + 1), delay);
      return () => clearTimeout(timeout);
    } else {
      const timeout = setTimeout(() => {
        setLines(0);
        round.current += 1;
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [lines]);

  return (
    <YStack backgroundColor="$color1" borderRadius="$3" borderWidth={1} borderColor="$color4" padding="$3" opacity={0.95} width={320} height={VIGNETTE_HEIGHT} justifyContent="flex-start">
      <XStack gap="$2" marginBottom="$2">
        <YStack width={8} height={8} borderRadius={4} backgroundColor="$red5" />
        <YStack width={8} height={8} borderRadius={4} backgroundColor="$yellow5" />
        <YStack width={8} height={8} borderRadius={4} backgroundColor="$green5" />
      </XStack>
      <YStack gap={0}>
        {deployLines.slice(0, lines).map((line, i) => (
          <Text
            key={`${round.current}-${i}`}
            fontSize={11}
            fontFamily="monospace"
            color={line.color === 'success' ? accent : line.color === 'summary' ? accent : '$color8'}
            lineHeight={18}
            fontWeight={line.color === 'summary' ? '600' : '400'}
          >
            {line.text || ' '}
          </Text>
        ))}
      </YStack>
    </YStack>
  );
}

/* ─── Vigil: Probe stream with occasional outage ─── */
const vigilProbes = [
  { label: 'cloudflare_dns', rtt: 7.9, ok: true },
  { label: 'google_dns_icmp', rtt: 24.1, ok: true },
  { label: 'router_icmp', rtt: 4.5, ok: true },
  { label: 'outlook_tcp443', rtt: 33.0, ok: true },
  { label: 'teams_icmp', rtt: 22.8, ok: true },
  { label: 'zoom_tcp443', rtt: 13.4, ok: true },
  { label: 'cloudflare_stun', rtt: 0, ok: false },
  { label: 'google_stun_udp', rtt: 0, ok: false },
  { label: 'outlook_icmp', rtt: 22.9, ok: true },
  { label: 'google_dns_udp', rtt: 37.2, ok: true },
];

export function VigilVignette({ accent }) {
  const [items, setItems] = useState(vigilProbes.slice(0, 4));
  const idx = useRef(4);

  useEffect(() => {
    const interval = setInterval(() => {
      setItems((prev) => {
        const next = vigilProbes[idx.current % vigilProbes.length];
        idx.current += 1;
        return [next, ...prev.slice(0, 3)];
      });
    }, 2200);
    return () => clearInterval(interval);
  }, []);

  return (
    <YStack backgroundColor="$color1" borderRadius="$3" borderWidth={1} borderColor="$color4" padding="$3" opacity={0.95} gap="$2" width={320} height={VIGNETTE_HEIGHT} justifyContent="flex-start">
      <XStack justifyContent="space-between" alignItems="center">
        <Text fontSize={10} color="$color6" fontWeight="600" letterSpacing={1}>PROBE STREAM</Text>
        <XStack gap="$1.5" alignItems="center">
          <YStack width={6} height={6} borderRadius={3} backgroundColor={accent} />
          <Text fontSize={9} color={accent} fontWeight="600">LIVE</Text>
        </XStack>
      </XStack>
      <YStack gap="$1">
        {items.map((item, i) => (
          <XStack key={`${item.label}-${i}-${idx.current}`} gap="$2" alignItems="center" paddingVertical={3} borderBottomWidth={1} borderBottomColor="$color3" opacity={i === 0 ? 1 : 0.75 - i * 0.1}>
            <Text fontSize={10} color={item.ok ? accent : '$red9'} fontFamily="monospace" fontWeight="700" width={10}>
              {item.ok ? '●' : '✗'}
            </Text>
            <Text fontSize={11} color="$color9" fontFamily="monospace" flex={1} numberOfLines={1}>{item.label}</Text>
            <Text fontSize={11} color={item.ok ? '$color8' : '$red9'} fontFamily="monospace">
              {item.ok ? `${item.rtt.toFixed(1)}ms` : 'timeout'}
            </Text>
          </XStack>
        ))}
      </YStack>
    </YStack>
  );
}

/* ─── Scuta: Install animation ─── */
export function ScutaVignette({ accent }) {
  const [progress, setProgress] = useState(0);
  const round = useRef(0);

  useEffect(() => {
    if (progress < 100) {
      const timeout = setTimeout(() => setProgress(Math.min(progress + 5, 100)), 120);
      return () => clearTimeout(timeout);
    } else {
      const timeout = setTimeout(() => {
        setProgress(0);
        round.current += 1;
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [progress]);

  const filled = Math.floor(progress / 5);
  const bar = '█'.repeat(filled) + '░'.repeat(20 - filled);

  return (
    <YStack backgroundColor="$color1" borderRadius="$3" borderWidth={1} borderColor="$color4" padding="$3" opacity={0.95} width={320} height={VIGNETTE_HEIGHT} justifyContent="flex-start">
      <XStack gap="$2" marginBottom="$2">
        <YStack width={8} height={8} borderRadius={4} backgroundColor="$red5" />
        <YStack width={8} height={8} borderRadius={4} backgroundColor="$yellow5" />
        <YStack width={8} height={8} borderRadius={4} backgroundColor="$green5" />
      </XStack>
      <YStack gap={0}>
        <Text fontSize={11} fontFamily="monospace" color="$color8" lineHeight={18}>
          $ scuta install pilum torch-cli
        </Text>
        <Text fontSize={11} fontFamily="monospace" color="$color8" lineHeight={18}> </Text>
        <Text fontSize={11} fontFamily="monospace" color="$color7" lineHeight={18}>
          ✓ Verified checksums (SHA-256)
        </Text>
        <Text fontSize={11} fontFamily="monospace" color={accent} lineHeight={18}>
          {bar} {progress}%
        </Text>
        {progress >= 100 && (
          <Text fontSize={11} fontFamily="monospace" color={accent} lineHeight={18} fontWeight="600">
            ✓ 2 tools installed in 4.2s
          </Text>
        )}
      </YStack>
    </YStack>
  );
}
